// src/components/Seguridad/Components/Tabs/Empleados/EmpleadosPuestoTab.jsx
import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Seguridad/Utils/Endpoints';
import { Models } from '../../../Seguridad/Utils/ModelsSeguridad';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../utils/SessionUser';

const TITLETAB = 'Asignación de Puestos';

// Endpoints
const endpointAssigned = ENDPOINTS.empleadosPuesto;
const endpointAssignedById = ENDPOINTS.empleadosPuestoById;
const endpointAvailable = ENDPOINTS.puestosSecurity;

// Models
const normalizeAssigned = Models.empleadosPuesto;
const normalizeAvailable = Models.puesto;

// Keys
const ID_ASSIGNED_KEY = 'idEmpleadoPuesto'; // Primary key of link table
const ID_ITEM_KEY = 'idPuesto';             // Foreign key of catalog
const FK_USER_KEY = 'idEmpleado';           // Foreign key of employee
const ITEM_NAME_KEY = 'nombre';

export default function EmpleadosPuestoTab({ fkValue, onClose, onDirtyChange, readOnly = false }) {
  const isSuperAdmin = isSessionSuperAdmin();
  const sessionEmpresaId = getSessionEmpresaId();

  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  // State for initial data (to calculate changes)
  const [initialAssigned, setInitialAssigned] = useState([]);

  // State for current local changes
  const [currentAssigned, setCurrentAssigned] = useState([]);

  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [idEmpresa, setIdEmpresa] = useState(null);
  const [showChildData, setShowChildData] = useState(false);

  const fetchEmpresaContext = async () => {
    try {
      const rawUser = sessionStorage.getItem('sigmaUser');
      if (rawUser) {
        const user = JSON.parse(rawUser);
        if (user.idUsuario) {
          // Consultar endpoint de empresas por usuario para obtener el contexto real
          const res = await axios.get(ENDPOINTS.empresasUsuario(user.idUsuario));
          if (res.data && res.data.length > 0) {
            setIdEmpresa(res.data[0].idEmpresa || res.data[0].IdEmpresa);
          }
        }
      }
    } catch (error) {
      console.error("Error al obtener contexto de empresa:", error);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const finalFilter = showChildData ? 'hijas' : 'propias';
      const [resAvailable, resAssigned] = await Promise.all([
        axios.get(endpointAvailable(finalFilter)),
        axios.get(endpointAssigned),
      ]);

      let items = (resAvailable?.data || []).map(normalizeAvailable);
      const rawAssigned = (resAssigned?.data || []).map(normalizeAssigned);

      // Filter for current employee
      const myAssigned = rawAssigned.filter(r => Number(r[FK_USER_KEY]) === Number(fkValue));

      // Fetch missing items metadata if we are viewing assignments that we don't have in our security context
      const missingIds = myAssigned
        .map(r => r[ID_ITEM_KEY])
        .filter(id => !items.find(i => i[ID_ITEM_KEY] === id));

      if (missingIds.length > 0) {
        const fetchPromises = missingIds.map(id => {
          const baseUrl = endpointAvailable('propias').split('?')[0].replace(/\/security$/i, '');
          return axios.get(`${baseUrl}/${id}`).catch(() => null);
        });
        const missingRes = await Promise.all(fetchPromises);
        const missingItems = missingRes.filter(res => res && res.data).map(res => normalizeAvailable(res.data));
        items = [...items, ...missingItems];
      }

      setAllItems(items);
      setInitialAssigned(myAssigned);
      setCurrentAssigned(myAssigned);
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: `Error al cargar ${TITLETAB}`, variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fkValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fkValue) {
      fetchEmpresaContext();
      load();
    }
  }, [fkValue, showChildData, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate available items (All Items - Current Assigned Items)
  const availableItems = useMemo(() => {
    const assignedIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
    const isSuperAdmin = isSessionSuperAdmin();
    const sessionEmpresaId = getSessionEmpresaId();

    return allItems.filter(r => {
      // 1. No estar asignado ya
      if (assignedIds.has(r[ID_ITEM_KEY])) return false;

      // 2. Aislamiento Multi-Tenant
      if (isSuperAdmin) return true;

      const idEmpresa = r.idEmpresa;
      const isOwn = !idEmpresa || Number(idEmpresa) === sessionEmpresaId;
      if (isOwn) return true;

      // Es de una empresa hija: solo se ve si el botón de "Ver Hijas" está activo
      return showChildData;
    });
  }, [allItems, currentAssigned, showChildData, isSuperAdmin, sessionEmpresaId]);

  // Enriched assigned items (add name from catalog)
  const enrichedAssigned = useMemo(() => {
    return currentAssigned.map(assigned => {
      const itemDef = allItems.find(i => i[ID_ITEM_KEY] === assigned[ID_ITEM_KEY]);
      const baseName = itemDef ? (itemDef[ITEM_NAME_KEY] || itemDef.nombre) : `Item #${assigned[ID_ITEM_KEY]}`;
      const empName = itemDef?.nombreEmpresa;
      return {
        ...assigned,
        _displayName: `(${empName || 'General'}): ${baseName}`,
      };
    });
  }, [currentAssigned, allItems]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (initialAssigned.length !== currentAssigned.length) return true;

    const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
    const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));

    for (let id of currentIds) {
      if (!initialIds.has(id)) return true;
    }
    return false;
  }, [initialAssigned, currentAssigned]);

  // Propagar estado "sucio"
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(hasChanges);
    }
  }, [hasChanges, onDirtyChange]);

  // --- Drag and Drop Handlers ---

  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);

    if (source === targetColumn) return;

    if (targetColumn === 'assigned' && source === 'available') {
      addLocal(item);
    } else if (targetColumn === 'available' && source === 'assigned') {
      removeLocal(item);
    }
  };

  // --- Local State Modifiers ---

  const addLocal = (item) => {
    const exists = currentAssigned.some(r => r[ID_ITEM_KEY] === item[ID_ITEM_KEY]);
    if (exists) return;

    const newAssignment = {
      [ID_ASSIGNED_KEY]: 0, // Temporary ID
      [FK_USER_KEY]: Number(fkValue),
      [ID_ITEM_KEY]: item[ID_ITEM_KEY],
      fechaAsignacion: new Date().toISOString().split('T')[0], // Default date as observed in previous file
    };

    setCurrentAssigned(prev => [...prev, newAssignment]);
  };

  const removeLocal = (assignedItem) => {
    setCurrentAssigned(prev => prev.filter(r => r[ID_ITEM_KEY] !== assignedItem[ID_ITEM_KEY]));
  };

  // --- Save / Cancel ---

  const handleCancel = () => {
    onClose?.();
    setCurrentAssigned(initialAssigned);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Identify to ADD
      const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
      const toAdd = currentAssigned.filter(r => !initialIds.has(r[ID_ITEM_KEY]));

      // 2. Identify to REMOVE
      const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
      const toRemove = initialAssigned.filter(r => !currentIds.has(r[ID_ITEM_KEY]));

      // 3. Execute requests
      const promises = [];

      // Additions
      const contextEmpresa = idEmpresa || 0;
      for (const item of toAdd) {
        const payload = {
          [ID_ASSIGNED_KEY]: 0,
          [FK_USER_KEY]: Number(fkValue),
          [ID_ITEM_KEY]: item[ID_ITEM_KEY],
          IdEmpresa: Number(contextEmpresa),
          fechaAsignacion: item.fechaAsignacion || new Date().toISOString().split('T')[0],
        };
        promises.push(axios.post(endpointAssigned, payload));
      }

      // Removals
      for (const item of toRemove) {
        const idToDelete = item[ID_ASSIGNED_KEY];
        if (idToDelete > 0) {
          const url = typeof endpointAssignedById === 'function'
            ? endpointAssignedById(idToDelete)
            : `${endpointAssignedById}/${idToDelete}`;
          promises.push(axios.delete(url));
        }
      }

      await Promise.all(promises);

      await load();
      await load();
      setAlertDialog({ open: true, title: 'Éxito', message: 'Cambios guardados correctamente.', variant: 'success' });

    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar los cambios.', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mini-crud">
      <div className="mini-topbar">
        <div>
          <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            {readOnly 
              ? 'Modo de solo lectura. No se permiten cambios.' 
              : 'Arrastra o usa los botones (+ / x) para modificar. Guarda al finalizar.'}
          </div>
        </div>
      </div>

      <div className="dnd-container">
        {/* Left: Available */}
        {!readOnly && (
          <div
            className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, 'available')}
            onDrop={(e) => handleDrop(e, 'available')}
          >
            <div className="dnd-header">Disponibles (Origen)</div>
            {loading && availableItems.length === 0 ? (
              <div className="dnd-empty">Cargando...</div>
            ) : availableItems.length === 0 ? (
              <div className="dnd-empty">No hay elementos disponibles</div>
            ) : (
              availableItems.map(item => (
                <div
                  key={item[ID_ITEM_KEY]}
                  className="dnd-item"
                  draggable={!saving && !readOnly}
                  onDragStart={(e) => handleDragStart(e, item, 'available')}
                >
                  <span>{`(${item.nombreEmpresa || 'General'}): ${item[ITEM_NAME_KEY] || item.nombre || `Item #${item[ID_ITEM_KEY]}`}`}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => addLocal(item)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.2em',
                        color: '#28a745',
                        cursor: 'pointer',
                        padding: '0 5px'
                      }}
                      title="Agregar"
                    >
                      ＋
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Right: Assigned */}
        <div
          className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
          style={readOnly ? { width: '100%', border: '1px solid #ddd', background: '#f9f9f9' } : {}}
          onDragOver={(e) => handleDragOver(e, 'assigned')}
          onDrop={(e) => handleDrop(e, 'assigned')}
        >
          <div className="dnd-header">{readOnly ? 'Elementos Asignados' : 'Asignados (Destino)'}</div>
          {loading && enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">{readOnly ? 'No hay elementos asignados' : 'Arrastra aquí para asignar'}</div>
          ) : (
            enrichedAssigned.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                style={readOnly ? { filter: 'grayscale(1)', opacity: 0.8, cursor: 'default' } : {}}
                draggable={!saving && !readOnly}
                onDragStart={(e) => handleDragStart(e, item, 'assigned')}
              >
                <span>{item._displayName}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeLocal(item)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2em',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '0 5px'
                    }}
                    title="Remover"
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
          <button
            className="roles-btn"
            onClick={handleCancel}
            type="button"
          >
            Cancelar / Reestablecer
          </button>
          <button
            className="btn-action"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{
              opacity: (!hasChanges || saving) ? 0.5 : 1,
              cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer'
            }}
            type="button"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      )}

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message || ""}
          variant={alertDialog.variant || "info"}
        />
      )}
    </div>
  );
}
